import { useBuildMonarchURL, useBuildRecognizeURL } from '@bonusly/url';

import { useIsRecognize } from '../../../hooks/use-is-recognize';

type LinkURLProps = {
	isRecognizeRoute?: boolean; // is the link going to a recognize page
	path: string; // the path of the link
};

type LinkURLResult = {
	isCrossDomain: boolean;
	url: string;
};

// Check if we're running as an MFE embedded in Special Sauce
// In MFE context, ALL navigation should be full page reloads since the pages are Rails-rendered
const isRunningAsMFE = () => {
	return typeof document !== 'undefined' && document.getElementById('user-sidebar-root') !== null;
};

export const useLinkNavigation = ({ path, isRecognizeRoute = false }: LinkURLProps): LinkURLResult => {
	const onRecognizePage = useIsRecognize();
	const monarchURL = useBuildMonarchURL();
	const recognizeURL = useBuildRecognizeURL();

	// When running as an MFE on Special Sauce pages, all navigation needs full page reload
	const isMFE = isRunningAsMFE();

	// Cross-domain navigation requires a full page reload via window.location.href
	// This happens when: on Recognize going to Monarch, OR on Monarch going to Recognize
	// OR when running as an MFE (all navigation needs full reload)
	const isCrossDomain = isMFE || (onRecognizePage && !isRecognizeRoute) || (!onRecognizePage && isRecognizeRoute);

	// if you are currently on a recognize page, and the link is going to a monarch page, return the monarch URL
	if (onRecognizePage && !isRecognizeRoute) {
		return { isCrossDomain, url: monarchURL(path) };
	}

	// if you are currently on a monarch page, and the link is going to a recognize page, return the recognize URL
	if (!onRecognizePage && isRecognizeRoute) {
		return { isCrossDomain, url: recognizeURL(path) };
	}

	// When in MFE mode on a Recognize page, ensure we use the full Recognize URL for same-domain navigation
	if (isMFE && onRecognizePage && isRecognizeRoute) {
		return { isCrossDomain, url: recognizeURL(path) };
	}

	// if you are currently on a recognize page, and the link is going to a recognize page, return the path
	// if you are currently on a monarch page, and the link is going to a monarch page, return the path
	return { isCrossDomain, url: path };
};
